/**
 * upgrade-plan.ts — the PREFLIGHT of `ss upgrade`: everything that can fail is
 * checked, and every planned rewrite computed IN MEMORY, before the first write.
 *
 * Historically the upgrade bumped the csproj files, ran `npm install`, then
 * crashed on a `JSON.parse` of an appsettings.json carrying one `// comment` —
 * a half-upgraded project with no way back. The preflight now:
 *
 * 1. runs the git guard (clean tree, no merge/rebase, protected branches);
 * 2. reads every configuration file as JSONC — a syntax error aborts with
 *    `file:line:col`;
 * 3. resolves the target versions: `--version` or the latest published,
 *    refuses a downgrade without `--allow-downgrade`, and makes the npm target
 *    FOLLOW the NuGet target (same version, or the same major.minor with a
 *    warning — never a silent `@next`);
 * 4. plans the bump of EVERY `SmartStack.*` PackageReference / PackageVersion
 *    (central `Directory.Packages.props` included) to versions that are
 *    actually published;
 * 5. plans the appsettings.json / .claude/settings.json reconciliation and the
 *    codemods, without writing them.
 *
 * Nothing here writes to disk. `ok:false` = the upgrade must stop, with
 * `errors[]` saying why.
 */
import fs from 'fs-extra';
import { join, relative, sep } from 'path';
import { glob } from 'glob';
import { parseJsonc } from './jsonc.js';
import { compareVersions, parseVersion, readProjectSmartStackVersion } from './smartstack-version.js';
import { checkGitSafety, resolveProtectedBranch, type ConfirmFn, type GitSafetyReport } from './upgrade-git-guard.js';
import {
  addMissingKeys,
  correctShippedDefaults,
  relocateLegacyKeys,
  removeObsoleteKeys,
  resolveTemplatePlaceholders,
} from './config-sync.js';
import { MIGRATION_IGNORE_GLOBS } from './upgrade-helpers.js';
import { loadInitState } from './file-tracker.js';
import { findApplicableMigrations } from '../migrations/registry.js';
import type { CodeMigration } from '../migrations/types.js';
import { normalizeNpmVersion } from './socle-compat.js';
import { projectCliFrom, readProjectConfig, type ProjectConfig } from './project-versions.js';
import type { VersionTriple } from './upgrade-backup.js';

export const SOCLE_NUGET_ID = 'SmartStack';
export const SOCLE_NPM_NAME = '@atlashub/smartstack';

/** Registry access, injected so the preflight is testable offline. Null = unreachable. */
export interface RegistryClient {
  nugetVersions(packageId: string): Promise<string[] | null>;
  npmVersions(packageName: string): Promise<string[] | null>;
}

export interface PreflightOptions {
  allowDirty?: boolean;
  allowProtected?: boolean;
  /** Explicit socle target (`--version`). */
  version?: string;
  allowDowngrade?: boolean;
  preview?: boolean;
  allowCommentLoss?: boolean;
  /** Dry run: git blockers become warnings, the protected-branch prompt is skipped. */
  dryRun?: boolean;
}

export interface PreflightInput {
  projectDir: string;
  /** The running CLI version — TO of the `cli` codemod axis. */
  cliVersion: string;
  /** `templates/project` of the CLI package. */
  templatesDir: string;
  options: PreflightOptions;
  registry: RegistryClient;
  io?: { isTTY: boolean; confirm?: ConfirmFn };
  /** Default true. Tests of the file planning can turn the git guard off. */
  checkGit?: boolean;
}

export interface PreflightIssue {
  message: string;
  file?: string;
  line?: number;
  col?: number;
}

export interface PackageEdit {
  /** Project-relative path, forward slashes. */
  rel: string;
  element: 'PackageReference' | 'PackageVersion';
  id: string;
  from: string;
  to: string;
}

export interface PlannedWrite {
  rel: string;
  before: string;
  after: string;
}

export interface JsonSyncPlan extends PlannedWrite {
  added: string[];
  removed: string[];
  relocated: string[];
  corrected: string[];
}

export interface SkippedJsonc {
  rel: string;
  reason: string;
  pending: string[];
}

export interface PlannedCodemods {
  migrations: Array<{ description: string; axis: CodeMigration['axis']; fromVersion: string; toVersion: string }>;
  files: Array<PlannedWrite & { descriptions: string[] }>;
}

export interface UpgradePlan {
  ok: boolean;
  errors: PreflightIssue[];
  warnings: string[];
  suggestions: string[];
  git: GitSafetyReport | null;
  config: { exists: boolean; value: ProjectConfig; hasComments: boolean; bom: boolean };
  baseNamespace: string;
  projectType: string;
  /** Absolute path of the web app carrying @atlashub/smartstack, or null. */
  frontendDir: string | null;
  versions: { from: VersionTriple; to: VersionTriple };
  /** The socle is already at the target. */
  socleUpToDate: boolean;
  npmUpToDate: boolean;
  packageEdits: PackageEdit[];
  /** The new content of every csproj / props file the bump rewrites. */
  packageWrites: PlannedWrite[];
  appsettings: JsonSyncPlan | null;
  claudeSettings: JsonSyncPlan | null;
  skippedJsonc: SkippedJsonc[];
  codemods: PlannedCodemods;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (exported for tests)
// ─────────────────────────────────────────────────────────────────────────────

const toRel = (projectDir: string, abs: string): string => relative(projectDir, abs).split(sep).join('/');

/** Latest version of a list sorted ascending (NuGet flat container / npm order is not trusted — re-sorted). */
export function pickLatest(versions: string[], preview: boolean): { version: string | null; fellBackToPrerelease: boolean } {
  const valid = versions.filter((v) => parseVersion(v)).sort(compareVersions);
  if (valid.length === 0) return { version: null, fellBackToPrerelease: false };
  if (preview) {
    const pre = valid.filter((v) => v.includes('-'));
    return { version: pre.length > 0 ? pre[pre.length - 1] : valid[valid.length - 1], fellBackToPrerelease: false };
  }
  const stable = valid.filter((v) => !v.includes('-'));
  if (stable.length > 0) return { version: stable[stable.length - 1], fellBackToPrerelease: false };
  return { version: valid[valid.length - 1], fellBackToPrerelease: true };
}

/**
 * The npm version that goes with a NuGet target: the same version when it is
 * published; else the highest of the same major.minor below it (a patch drift
 * the compat matrix tolerates, reported); else null (no compatible release).
 */
export function pickNpmTarget(npmVersions: string[], socleTarget: string): { version: string | null; exact: boolean } {
  if (npmVersions.includes(socleTarget)) return { version: socleTarget, exact: true };
  const target = parseVersion(socleTarget);
  if (!target) return { version: null, exact: false };
  const sameMinor = npmVersions
    .filter((v) => {
      const p = parseVersion(v);
      return p !== null && p.major === target.major && p.minor === target.minor && compareVersions(v, socleTarget) <= 0;
    })
    .sort(compareVersions);
  return { version: sameMinor.length > 0 ? sameMinor[sameMinor.length - 1] : null, exact: false };
}

export interface SmartStackPackageRef {
  element: 'PackageReference' | 'PackageVersion';
  id: string;
  version: string | null;
  /** Offsets of the Version attribute VALUE in the file (for the in-place edit). */
  valueStart: number;
  valueEnd: number;
}

/** Every `SmartStack` / `SmartStack.*` PackageReference or PackageVersion of an MSBuild file. */
export function findSmartStackPackageRefs(content: string): SmartStackPackageRef[] {
  const refs: SmartStackPackageRef[] = [];
  const tagRe = /<(PackageReference|PackageVersion)\b([^>]*?)\/?>/g;
  let tag: RegExpExecArray | null;
  while ((tag = tagRe.exec(content)) !== null) {
    const attrs = tag[2];
    const include = /\b(?:Include|Update)\s*=\s*"([^"]+)"/i.exec(attrs);
    if (!include || !/^SmartStack(\.|$)/i.test(include[1])) continue;
    const version = /\bVersion\s*=\s*"([^"]*)"/.exec(attrs);
    const attrsStart = tag.index + 1 + tag[1].length;
    if (version) {
      const valueStart = attrsStart + version.index + version[0].indexOf('"') + 1;
      refs.push({ element: tag[1] as SmartStackPackageRef['element'], id: include[1], version: version[1], valueStart, valueEnd: valueStart + version[1].length });
    } else {
      refs.push({ element: tag[1] as SmartStackPackageRef['element'], id: include[1], version: null, valueStart: -1, valueEnd: -1 });
    }
  }
  return refs;
}

/** Rewrite the given version values (offsets from {@link findSmartStackPackageRefs}). */
export function applyVersionEdits(content: string, edits: Array<{ valueStart: number; valueEnd: number; to: string }>): string {
  let out = content;
  for (const e of [...edits].sort((a, b) => b.valueStart - a.valueStart)) {
    out = out.slice(0, e.valueStart) + e.to + out.slice(e.valueEnd);
  }
  return out;
}

/** Serialize a JSON tree the way the original file was laid out (BOM, CRLF, trailing newline). */
export function serializeLike(original: string, value: unknown): string {
  const bom = original.charCodeAt(0) === 0xfeff;
  const crlf = original.includes('\r\n');
  const trailing = /\r?\n$/.test(original);
  let out = JSON.stringify(value, null, 2) + (trailing ? '\n' : '');
  if (crlf) out = out.replace(/\n/g, '\r\n');
  return (bom ? '﻿' : '') + out;
}

function issueFromJsonc(rel: string, r: { message: string; line?: number; col?: number }): PreflightIssue {
  const where = r.line ? `${rel}:${r.line}:${r.col ?? 1}` : rel;
  return { file: rel, line: r.line, col: r.col, message: `${where} — ${r.message.replace(`${rel}: `, '')}` };
}

async function readJsonc(projectDir: string, rel: string, errors: PreflightIssue[]): Promise<
  | { ok: true; raw: string; value: Record<string, unknown>; hasComments: boolean }
  | { ok: false }
  | null
> {
  const abs = join(projectDir, ...rel.split('/'));
  if (!(await fs.pathExists(abs))) return null;
  const raw = await fs.readFile(abs, 'utf-8');
  const parsed = parseJsonc<Record<string, unknown>>(raw, rel);
  if (!parsed.ok) {
    errors.push(issueFromJsonc(rel, parsed));
    return { ok: false };
  }
  if (parsed.value === null || typeof parsed.value !== 'object' || Array.isArray(parsed.value)) {
    errors.push({ file: rel, message: `${rel} — expected a JSON object` });
    return { ok: false };
  }
  return { ok: true, raw, value: parsed.value, hasComments: parsed.hasComments };
}

/** Infer the base namespace from `src/<Ns>.<Layer>/*.csproj` referencing SmartStack (project without config.json). */
async function detectFromCsproj(projectDir: string): Promise<{ baseNamespace: string; version: string } | null> {
  const srcDir = join(projectDir, 'src');
  if (!(await fs.pathExists(srcDir))) return null;
  for (const entry of await fs.readdir(srcDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    for (const file of await fs.readdir(join(srcDir, entry.name))) {
      if (!file.endsWith('.csproj')) continue;
      const refs = findSmartStackPackageRefs(await fs.readFile(join(srcDir, entry.name, file), 'utf-8'));
      const main = refs.find((r) => r.id.toLowerCase() === 'smartstack' && r.version);
      if (main) {
        const dot = entry.name.lastIndexOf('.');
        return { baseNamespace: dot > 0 ? entry.name.slice(0, dot) : entry.name, version: main.version! };
      }
    }
  }
  return null;
}

/** First `web/<x>/package.json` depending on @atlashub/smartstack (parsed as JSONC). */
async function findFrontend(projectDir: string, errors: PreflightIssue[]): Promise<{ dir: string; npmFrom: string | null } | null> {
  const webDir = join(projectDir, 'web');
  if (!(await fs.pathExists(webDir))) return null;
  for (const entry of await fs.readdir(webDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const rel = `web/${entry.name}/package.json`;
    const pkg = await readJsonc(projectDir, rel, errors);
    if (!pkg || !pkg.ok) continue;
    const deps = (pkg.value.dependencies ?? {}) as Record<string, string>;
    if (deps[SOCLE_NPM_NAME]) {
      return { dir: join(webDir, entry.name), npmFrom: normalizeNpmVersion(deps[SOCLE_NPM_NAME]) };
    }
  }
  return null;
}

/** Plan a JSON reconciliation; refuse to drop comments unless allowed. */
function planJsonRewrite(
  rel: string,
  file: { raw: string; value: Record<string, unknown>; hasComments: boolean },
  next: Record<string, unknown>,
  changes: { added: string[]; removed: string[]; relocated: string[]; corrected: string[] },
  allowCommentLoss: boolean,
  warnings: string[],
  skipped: SkippedJsonc[],
): JsonSyncPlan | null {
  const pending = [
    ...changes.added.map((k) => `+ ${k}`),
    ...changes.removed.map((k) => `- ${k}`),
    ...changes.relocated.map((k) => `↻ ${k}`),
    ...changes.corrected.map((k) => `~ ${k}`),
  ];
  if (pending.length === 0) return null;
  if (file.hasComments && !allowCommentLoss) {
    skipped.push({
      rel,
      reason: 'contains comments that a JSON rewrite would drop — not rewritten (re-run with --allow-comment-loss, or apply the changes by hand)',
      pending,
    });
    warnings.push(`${rel}: ${pending.length} pending change(s) NOT applied — the file has comments (use --allow-comment-loss to rewrite it anyway).`);
    return null;
  }
  if (file.hasComments) warnings.push(`${rel}: rewritten WITHOUT its comments (--allow-comment-loss) — the pre-upgrade copy is in the backup.`);
  return { rel, before: file.raw, after: serializeLike(file.raw, next), ...changes };
}

// ─────────────────────────────────────────────────────────────────────────────
// Preflight
// ─────────────────────────────────────────────────────────────────────────────

export async function runUpgradePreflight(input: PreflightInput): Promise<UpgradePlan> {
  const { projectDir, options, registry } = input;
  const errors: PreflightIssue[] = [];
  const warnings: string[] = [];
  const suggestions: string[] = [];
  const skippedJsonc: SkippedJsonc[] = [];

  const plan: UpgradePlan = {
    ok: false,
    errors,
    warnings,
    suggestions,
    git: null,
    config: { exists: false, value: {}, hasComments: false, bom: false },
    baseNamespace: '',
    projectType: 'fullstack',
    frontendDir: null,
    versions: { from: { socle: null, npm: null, cli: null }, to: { socle: null, npm: null, cli: input.cliVersion } },
    socleUpToDate: false,
    npmUpToDate: true,
    packageEdits: [],
    packageWrites: [],
    appsettings: null,
    claudeSettings: null,
    skippedJsonc,
    codemods: { migrations: [], files: [] },
  };

  // ── 1. Git guard (no prompt yet) ─────────────────────────────────────────
  if (input.checkGit !== false) {
    const git = checkGitSafety(projectDir, { allowDirty: options.allowDirty, allowProtected: options.allowProtected });
    plan.git = git;
    warnings.push(...git.warnings);
    suggestions.push(...git.suggestions);
    for (const b of git.blockers) {
      if (options.dryRun) warnings.push(`[would block] ${b}`);
      else errors.push({ message: b });
    }
  }

  // ── 2. Project + config (JSONC) ──────────────────────────────────────────
  const configRead = await readProjectConfig(projectDir);
  if (configRead && !configRead.ok) {
    errors.push(issueFromJsonc('.smartstack/config.json', configRead));
  } else if (configRead && configRead.ok) {
    plan.config = { exists: true, value: configRead.value, hasComments: configRead.hasComments, bom: configRead.bom };
    if (configRead.hasComments && !options.allowCommentLoss) {
      errors.push({
        file: '.smartstack/config.json',
        message: '.smartstack/config.json has comments — the upgrade records its versions there and would drop them. Remove the comments, or pass --allow-comment-loss.',
      });
    }
  }
  const csprojDetection = await detectFromCsproj(projectDir);
  if (!plan.config.exists) {
    if (!csprojDetection && !configRead) {
      errors.push({ message: 'Not a SmartStack project (no .smartstack/config.json, no src/*/*.csproj referencing SmartStack). Run ss upgrade from the project root.' });
      return plan;
    }
    if (csprojDetection) {
      plan.config.value = {
        projectType: 'fullstack',
        dbContext: `${csprojDetection.baseNamespace}DbContext`,
        baseNamespace: csprojDetection.baseNamespace,
        smartStackVersion: csprojDetection.version,
      };
      warnings.push('No .smartstack/config.json — project detected from its .csproj files (run ss init --here to create the config).');
    }
  }
  plan.baseNamespace = String(plan.config.value.baseNamespace ?? csprojDetection?.baseNamespace ?? '');
  plan.projectType = String(plan.config.value.projectType ?? 'fullstack');

  // ── 3. Local files: frontend, appsettings, settings (all JSONC) ──────────
  const frontend = await findFrontend(projectDir, errors);
  plan.frontendDir = frontend?.dir ?? null;

  const appRel = plan.baseNamespace ? `src/${plan.baseNamespace}.Api/appsettings.json` : null;
  const appFile = appRel ? await readJsonc(projectDir, appRel, errors) : null;
  const settingsRel = '.claude/settings.json';
  const settingsFile = await readJsonc(projectDir, settingsRel, errors);

  const templatePath = (name: string) => join(input.templatesDir, name);
  if (appRel && appFile && appFile.ok) {
    if (await fs.pathExists(templatePath('appsettings.json.template'))) {
      const rawTemplate = resolveTemplatePlaceholders(await fs.readFile(templatePath('appsettings.json.template'), 'utf-8'), plan.baseNamespace);
      const template = parseJsonc<Record<string, unknown>>(rawTemplate, 'appsettings.json.template');
      if (!template.ok) {
        errors.push({ message: `CLI template appsettings.json.template is invalid: ${template.message}` });
      } else {
        const next = structuredClone(appFile.value);
        const relocated = relocateLegacyKeys(next);
        const added = addMissingKeys(next, template.value);
        const removed = removeObsoleteKeys(next);
        const corrected = correctShippedDefaults(next);
        plan.appsettings = planJsonRewrite(appRel, appFile, next, { added, removed, relocated, corrected }, !!options.allowCommentLoss, warnings, skippedJsonc);
      }
    } else {
      warnings.push('appsettings.json.template not found in the CLI package — appsettings sync skipped.');
    }
  }
  if (await fs.pathExists(templatePath('claude-settings.json.template')) && !(settingsFile && !settingsFile.ok)) {
    const template = parseJsonc<Record<string, unknown>>(await fs.readFile(templatePath('claude-settings.json.template'), 'utf-8'), 'claude-settings.json.template');
    if (!template.ok) {
      errors.push({ message: `CLI template claude-settings.json.template is invalid: ${template.message}` });
    } else {
      const current = settingsFile && settingsFile.ok ? settingsFile : { raw: '', value: {}, hasComments: false };
      const next = structuredClone(current.value);
      const added = addMissingKeys(next, template.value);
      const planned = planJsonRewrite(settingsRel, current, next, { added, removed: [], relocated: [], corrected: [] }, !!options.allowCommentLoss, warnings, skippedJsonc);
      if (planned && !settingsFile) planned.after = JSON.stringify(next, null, 2) + '\n';
      plan.claudeSettings = planned;
    }
  }

  // ── 4. Versions ──────────────────────────────────────────────────────────
  const socleFrom = await readProjectSmartStackVersion(projectDir);
  const initState = await loadInitState(projectDir);
  const cliFrom = projectCliFrom(plan.config.value, initState?.cliVersion ?? null);
  plan.versions.from = { socle: socleFrom, npm: frontend?.npmFrom ?? null, cli: cliFrom };

  let socleTo: string | null = null;
  const nugetVersions = await registry.nugetVersions(SOCLE_NUGET_ID);
  if (!nugetVersions) {
    errors.push({ message: 'Could not fetch the SmartStack versions from NuGet — check the network, then retry.' });
  } else if (options.version) {
    const wanted = nugetVersions.find((v) => v.toLowerCase() === options.version!.toLowerCase());
    if (!wanted) errors.push({ message: `SmartStack ${options.version} is not published on NuGet.` });
    else socleTo = wanted;
  } else {
    const latest = pickLatest(nugetVersions, !!options.preview);
    if (!latest.version) errors.push({ message: 'NuGet lists no version of SmartStack.' });
    else {
      socleTo = latest.version;
      if (latest.fellBackToPrerelease) warnings.push(`No stable SmartStack release — targeting the prerelease ${socleTo}.`);
    }
  }

  if (socleTo && socleFrom && compareVersions(socleTo, socleFrom) < 0 && !options.allowDowngrade) {
    errors.push({
      message: `The target SmartStack ${socleTo} is OLDER than the project's ${socleFrom} — refusing to downgrade. ` +
        `${parseVersion(socleFrom)?.prerelease ? 'The project is on a prerelease: add --preview. ' : ''}Pass --allow-downgrade if this is intended.`,
    });
  }
  plan.versions.to.socle = socleTo;
  plan.socleUpToDate = socleTo !== null && socleFrom === socleTo;

  if (frontend && socleTo) {
    const npmVersions = await registry.npmVersions(SOCLE_NPM_NAME);
    if (!npmVersions) {
      errors.push({ message: `Could not fetch the ${SOCLE_NPM_NAME} versions from npm — check the network, then retry.` });
    } else {
      const npm = pickNpmTarget(npmVersions, socleTo);
      if (!npm.version) {
        errors.push({
          message: `${SOCLE_NPM_NAME} has no release compatible with SmartStack ${socleTo} on npm yet (same major.minor required) — retry once it is published.`,
        });
      } else {
        if (!npm.exact) warnings.push(`${SOCLE_NPM_NAME}@${socleTo} is not on npm — using ${npm.version} (same major.minor, patch drift).`);
        if (frontend.npmFrom && compareVersions(npm.version, frontend.npmFrom) < 0 && !options.allowDowngrade) {
          errors.push({ message: `The npm target ${npm.version} is OLDER than the frontend's ${frontend.npmFrom} — refusing to downgrade (--allow-downgrade).` });
        }
        plan.versions.to.npm = npm.version;
        plan.npmUpToDate = frontend.npmFrom === npm.version;
      }
    }
  } else if (frontend && !frontend.npmFrom) {
    plan.npmUpToDate = false;
  }

  // ── 5. SmartStack.* family bump ──────────────────────────────────────────
  if (socleTo) {
    const files = await glob(['**/*.csproj', '**/Directory.Packages.props'], {
      cwd: projectDir,
      absolute: true,
      nodir: true,
      ignore: [...MIGRATION_IGNORE_GLOBS, '**/.smartstack/**'],
    });
    const published = new Map<string, Promise<string[] | null>>();
    const isPublished = async (id: string): Promise<boolean> => {
      if (id.toLowerCase() === SOCLE_NUGET_ID.toLowerCase()) return true;
      const key = id.toLowerCase();
      if (!published.has(key)) published.set(key, registry.nugetVersions(id));
      const list = await published.get(key)!;
      return !!list && list.some((v) => v.toLowerCase() === socleTo!.toLowerCase());
    };
    const hasCentral = files.some((f) => f.endsWith('Directory.Packages.props'));
    for (const abs of files.sort()) {
      const rel = toRel(projectDir, abs);
      const content = await fs.readFile(abs, 'utf-8');
      const edits: Array<{ valueStart: number; valueEnd: number; to: string }> = [];
      for (const ref of findSmartStackPackageRefs(content)) {
        if (ref.version === null) {
          if (ref.element === 'PackageReference' && !hasCentral) {
            warnings.push(`${rel}: ${ref.id} has no Version attribute — not bumped (set its version by hand).`);
          }
          continue;
        }
        if (ref.version === socleTo) continue;
        if (!parseVersion(ref.version)) {
          warnings.push(`${rel}: ${ref.id} Version="${ref.version}" is not a plain version (property or range) — not bumped.`);
          continue;
        }
        const isMain = ref.id.toLowerCase() === SOCLE_NUGET_ID.toLowerCase();
        if (compareVersions(socleTo, ref.version) < 0 && !options.allowDowngrade) {
          if (!isMain) warnings.push(`${rel}: ${ref.id} ${ref.version} is newer than ${socleTo} — left as is.`);
          continue;
        }
        if (!(await isPublished(ref.id))) {
          warnings.push(`${rel}: ${ref.id} ${socleTo} is not published on NuGet — left at ${ref.version}.`);
          continue;
        }
        edits.push({ valueStart: ref.valueStart, valueEnd: ref.valueEnd, to: socleTo });
        plan.packageEdits.push({ rel, element: ref.element, id: ref.id, from: ref.version, to: socleTo });
      }
      if (edits.length > 0) plan.packageWrites.push({ rel, before: content, after: applyVersionEdits(content, edits) });
    }
  }

  // ── 6. Codemods, in memory ───────────────────────────────────────────────
  const migrations = findApplicableMigrations({
    socle: socleTo ? { from: socleFrom, to: socleTo } : undefined,
    cli: { from: cliFrom, to: input.cliVersion },
  });
  plan.codemods.migrations = migrations.map((m) => ({ description: m.description, axis: m.axis, fromVersion: m.fromVersion, toVersion: m.toVersion }));
  const codemod = await planCodemods(projectDir, migrations);
  errors.push(...codemod.errors);
  plan.codemods.files = codemod.files;

  // ── 7. Protected branch: ask last, only when everything else is green ────
  if (errors.length === 0 && plan.git?.needsConfirmation) {
    if (options.dryRun) {
      warnings.push(`[would ask] ${plan.git.branch} is a protected branch — a real run asks for confirmation (or needs --allow-protected-branch).`);
    } else {
      const refusal = await resolveProtectedBranch(plan.git, input.io ?? { isTTY: false });
      if (refusal) errors.push({ message: refusal });
    }
  }

  plan.ok = errors.length === 0;
  return plan;
}

/**
 * Run the applicable codemods against the CURRENT content of the project, in
 * memory. Chained per file (a later migration sees the earlier one's output).
 * A `transform` that throws is a preflight error naming the file.
 */
export async function planCodemods(
  projectDir: string,
  migrations: CodeMigration[],
): Promise<{ files: PlannedCodemods['files']; errors: PreflightIssue[] }> {
  const original = new Map<string, string>();
  const current = new Map<string, string>();
  const descriptions = new Map<string, string[]>();
  const errors: PreflightIssue[] = [];

  for (const migration of migrations) {
    for (const fileMigration of migration.migrations) {
      const files = await glob(fileMigration.pattern, { cwd: projectDir, absolute: true, nodir: true, ignore: MIGRATION_IGNORE_GLOBS });
      for (const abs of files.sort()) {
        const rel = toRel(projectDir, abs);
        if (!current.has(rel)) {
          const content = await fs.readFile(abs, 'utf-8');
          original.set(rel, content);
          current.set(rel, content);
        }
        const content = current.get(rel)!;
        try {
          if (!fileMigration.detect(content, abs)) continue;
          const next = fileMigration.transform(content, abs);
          if (next !== content) {
            current.set(rel, next);
            descriptions.set(rel, [...(descriptions.get(rel) ?? []), fileMigration.description]);
          }
        } catch (err) {
          errors.push({ file: rel, message: `${rel} — code migration "${fileMigration.description}" failed: ${err instanceof Error ? err.message : String(err)}` });
        }
      }
    }
  }

  const files: PlannedCodemods['files'] = [];
  for (const [rel, after] of current) {
    const before = original.get(rel)!;
    if (after !== before) files.push({ rel, before, after, descriptions: descriptions.get(rel) ?? [] });
  }
  return { files, errors };
}
